import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { tokens, type } from '../theme';
import CompetitionChip from './CompetitionChip';
import { formatWeather, describeWeather } from '../utils/weather';
import HourlyForecast from './HourlyForecast';
import { formatMiles } from '../utils/distance';
import { Link } from 'react-router-dom';
import { shootoutSuffix, shootoutTitle } from '../utils/shootout';
import { Card, CardContent, Box, Typography, Avatar } from '@mui/material';
import { TAP_TARGET } from '../utils/tapTarget';

// Shared so the Schedule and Team Detail lists cannot drift apart again — they
// previously rendered two different fixture layouts with different vertical costs.
//
// Two details worth keeping:
//  * Names clamp to two lines rather than truncating on one. Club names in this
//    league run long ("Cincinnati United Premier Soccer Club"), and a hard
//    ellipsis at phone width cuts most of them mid-word.
//  * The touch target is expanded with an ::after overlay instead of a tall row,
//    so links clear 44px without the card growing.
// Venue label. Only ~11 of ~1,900 venues in the data carry a full street address,
// and those are pure clutter in a list ("SF Glens TI Field - 620 Ave. I, Treasure
// Island, San Francisco, CA 94132"). Field designations ("- Field 24", "- Turf")
// are useful for finding the right pitch, so they stay. The full string is kept on
// the link's title and in the map query.
const formatVenue = (loc) => {
  if (!loc) return null;
  const s = String(loc).trim();
  if (!s || s === '-') return null;
  if (!/,|\b\d{5}\b/.test(s)) return s;
  return s.split(' - ')[0].trim() || s;
};

const nameSx = (emphasis, isMobile, align) => ({
  fontWeight: emphasis ? 700 : 400,
  color: emphasis ? tokens.navy : 'text.primary',
  textDecoration: 'none',
  fontSize: isMobile ? type.bodySm : type.body,
  lineHeight: 1.25,
  textAlign: align,
  minWidth: 0,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  ...TAP_TARGET,
});

const TeamSide = ({ id, name, logo, emphasis, isMobile, align, linked, suffix }) => {
  const label = suffix ? `${name} ${suffix}` : name;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', flex: 1, minWidth: 0,
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      gap: 1,
    }}>
      {align === 'right' && (
        <Typography
          component={linked ? Link : 'span'}
          to={linked ? `/team/${id}` : undefined}
          sx={nameSx(emphasis, isMobile, 'right')}
        >{label}</Typography>
      )}
      {logo && (
        <Avatar src={logo} alt={name}
          sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, flexShrink: 0 }} />
      )}
      {align !== 'right' && (
        <Typography
          component={linked ? Link : 'span'}
          to={linked ? `/team/${id}` : undefined}
          sx={nameSx(emphasis, isMobile, 'left')}
        >{label}</Typography>
      )}
    </Box>
  );
};

const FixtureCard = ({
  game,
  isMobile: isMobileProp,
  score,          // optional override; normally derived from the game
  accentColor = null,
  highlightHome = false,
  highlightAway = false,
  linkHome = true,
  linkAway = true,
  ageSuffix = null,
  selected = false,
  cardRef,
  // 'full' spells out the date; 'timeOnly' omits it because a sticky date header
  // above the group already supplies it.
  dateStyle = 'full',
  // 'card' stands alone (Team Detail); 'row' renders bare for use inside a day card.
  variant = 'card',
  // Game-time forecast, when the fixture is inside the ~7 day NWS horizon.
  weather = null,
  // Straight-line miles from the viewer, computed in the browser.
  distanceMiles = null,
}) => {
  // Layout follows the viewport, not a prop. Callers used to have to pass isMobile
  // and My Team simply did not, so the card silently laid itself out for desktop on
  // a phone — 24px margins where every card around it had 12px. A prop is still
  // honoured when given, so existing callers behave exactly as before, but
  // forgetting it can no longer produce wrong spacing.
  const autoMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const isMobile = isMobileProp !== undefined ? isMobileProp : autoMobile;
  const gameDate = new Date(game.date);

  const played = game.home_score !== 'TBD'
    && game.home_score !== null
    && game.home_score !== undefined;
  // MLS NEXT publishes fixtures before kickoff times are set, and those arrive as
  // exactly midnight. Rendering "12:00 AM" would imply a midnight kickoff.
  const timeUnset = gameDate.getHours() === 0 && gameDate.getMinutes() === 0;
  // How far away a fixture is only matters before you travel to it. On a played
  // game the distance is just noise on the row, so it is dropped.
  //
  // Kickoff times are stored naive and venue-local while Date.now() is the
  // viewer's clock, so this can be off by the timezone difference between them.
  // That is acceptable for deciding "have they been yet" — at worst a distance
  // lingers or disappears a few hours early — and it matches how the forecast
  // horizon is judged.
  const upcoming = gameDate.getTime() >= Date.now();
  const kickoff = timeUnset
    ? 'TBC'
    : gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const centre = score !== undefined && score !== null
    ? score
    : (played ? `${game.home_score} : ${game.away_score}` : kickoff);

  // The meta line is a variable number of segments — date or kickoff, weather,
  // distance — and any of them can be absent. Composing them and joining beats
  // prefixing each with " · ", which left a dangling bullet whenever the first
  // segment was empty: an unplayed fixture in timeOnly mode renders no time, and
  // once climate normals gave those rows something to show, every one of them
  // read "· ~64°/47°".
  const dateLabel = dateStyle === 'timeOnly'
    ? (played ? kickoff : '')
    : `${gameDate.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      })}${played && !timeUnset ? ` at ${kickoff}` : ''}`;

  const metaParts = [];
  if (dateLabel) metaParts.push(dateLabel);
  if (formatWeather(weather)) {
    // A recorded forecast has hours either side of kickoff worth showing; a
    // climate normal describes a date rather than a day, so it has none and
    // keeps the plain tooltip.
    const hasHourly = weather.source === 'forecast' && game.match_id;
    metaParts.push(
      hasHourly ? (
        <HourlyForecast matchId={game.match_id} fallback={describeWeather(weather)}>
          {formatWeather(weather)}
        </HourlyForecast>
      ) : (
        <Box component="span" title={describeWeather(weather)}>
          {formatWeather(weather)}
        </Box>
      ),
    );
  }
  // How far away a fixture is only matters before you travel to it.
  if (upcoming && formatMiles(distanceMiles)) {
    metaParts.push(
      <Box
        component="span"
        title={`${formatMiles(distanceMiles)} in a straight line from you`}
        sx={{ color: 'text.secondary' }}
      >
        {formatMiles(distanceMiles)}
      </Box>,
    );
  }

  const body = (
      <Box sx={{
        p: isMobile ? 1.5 : 2,
        borderLeft: accentColor ? `4px solid ${accentColor}` : 'none',
        backgroundColor: selected ? tokens.navyTint : 'transparent',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TeamSide
            id={game.home_team_id} name={game.home_team} logo={game.home_logo}
            emphasis={highlightHome} isMobile={isMobile} align="left"
            linked={linkHome} suffix={ageSuffix}
          />
          <Box sx={{ flexShrink: 0, minWidth: isMobile ? 54 : 66, textAlign: 'center' }}>
            <Typography sx={{
              fontWeight: played ? 700 : 500,
              fontSize: played ? type.lead : type.caption,
              color: played ? tokens.ink : tokens.inkMuted,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}>{centre}</Typography>
            {/* Flex only. The scoreline stays the draw it was — this says who took
                the extra point, which the 2-2 on its own cannot. */}
            {shootoutSuffix(game) && (
              <Typography
                title={shootoutTitle(game)}
                sx={{
                  fontSize: type.label, color: tokens.inkMuted,
                  whiteSpace: 'nowrap', lineHeight: 1.2,
                }}
              >{shootoutSuffix(game)}</Typography>
            )}
          </Box>
          <TeamSide
            id={game.away_team_id} name={game.away_team} logo={game.away_logo}
            emphasis={highlightAway} isMobile={isMobile} align="right"
            linked={linkAway} suffix={ageSuffix}
          />
        </Box>

        {/* Same three-slot geometry as the row above — equal flex flanks with a
            shrink-proof centre — so the date sits on the kickoff time's centre
            line on every row. Previously this was space-between with three
            children, which positions the middle one from the leftover space, so
            the date drifted left or right depending on how long it happened to
            be. */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 0.75,
        }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
            <CompetitionChip
              competitionType={game.competition_type}
              ageGroup={game.age_group}
            />
          </Box>
          <Typography variant="caption" sx={{
            color: 'text.secondary', fontSize: type.caption,
            whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'center',
          }}>
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && ' · '}
                {part}
              </React.Fragment>
            ))}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
            {/* The venue name goes to the venue page when we know which ground
                this is, and to Maps when we only have the feed's free text. A
                located venue has a page worth reaching: every fixture there that
                day, across age groups, plus the weather. */}
            {formatVenue(game.location) && (
              <Typography
                variant="caption"
                {...(game.venue?.id
                  ? { component: Link, to: `/venue/${game.venue.id}` }
                  : {
                      component: 'a',
                      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    })}
                title={game.location}
                sx={{
                  color: tokens.navy, fontSize: type.caption, textDecoration: 'none',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  minWidth: 0, '&:hover': { textDecoration: 'underline' },
                }}
              >{formatVenue(game.location)}</Typography>
            )}
          </Box>
        </Box>
      </Box>
  );

  if (variant === 'row') return body;

  return (
    <Card
      ref={cardRef}
      sx={{
        mb: 1.5,
        boxShadow: selected ? '0 2px 10px rgba(13,32,61,0.22)' : undefined,
        overflow: 'hidden',
      }}
    >
      {body}
    </Card>
  );
};

export default FixtureCard;
